import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import Loading from "src/components/Loading";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { SortDescriptor, orderBy, CompositeFilterDescriptor, filterBy } from "@progress/kendo-data-query";
import GoHighLevelNav from "./GoHighLevelNav";
import styles from "./GoHighLevel.module.css";

interface SubAccount {
  locationId: string;
  locationName: string;
  isActive: boolean;
  inaccessibleSince: string | null;
  contactCount: number | null;
  contactCountUpdatedAt: string | null;
  bulkScrubEnabled: boolean;
  bulkScrubSchedule: string | null;
  bulkScrubTimeZone: string | null;
  bulkScrubLastRunAt: string | null;
  autoScrubOnContactCreate: boolean;
  createNoteOnContactCreate: boolean;
  rescrubIntervalHours: number;
  scrubLimit: number | null;
}

interface AgencySummary {
  companyId: string;
  agencyName: string;
  isActive: boolean;
  isInstalled: boolean;
  hasDncCredentials: boolean;
  subAccountCount: number;
  totalContacts: number;
  scrubLimit: number | null;
}

interface AgencySettings {
  companyId: string;
  agencyName: string;
  dncScrubLoginId: string;
  dncScrubCampaignId: string;
  isActive: boolean;
  isConnected: boolean;
}

const FLAG_FIELDS: Record<string, string> = {
  bulkScrubEnabled: "BulkScrubEnabled",
  autoScrubOnContactCreate: "AutoScrubOnContactCreate",
  createNoteOnContactCreate: "CreateNoteOnContactCreate",
};

const AgencyDetail: React.FC = () => {
  const { companyId = "" } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState<AgencySummary | null>(null);
  const [settings, setSettings] = useState<AgencySettings | null>(null);
  const [locations, setLocations] = useState<SubAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savingAgency, setSavingAgency] = useState(false);
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: "contactCount", dir: "desc" }]);
  const [gridFilter, setGridFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      const [result, agencySettings] = await Promise.all([
        callApi(`sysadmin/gohighlevel/agencies/${companyId}/locations`, HttpMethod.GET),
        callApi(`sysadmin/gohighlevel/agencies/${companyId}/settings`, HttpMethod.GET),
      ]);
      setAgency(result.agency);
      setLocations(result.locations);
      setSettings(agencySettings);
    } catch {
      toast.error("Failed to load agency subaccounts.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const saveAgency = async () => {
    if (!settings) return;
    setSavingAgency(true);
    try {
      await callApi(`sysadmin/gohighlevel/agencies/${companyId}/settings`, HttpMethod.PUT, settings);
      toast.success("Agency settings saved.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save agency settings.");
    } finally {
      setSavingAgency(false);
    }
  };

  const updateSetting = <K extends keyof AgencySettings>(key: K, value: AgencySettings[K]) =>
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev));

  useEffect(() => {
    load();
  }, [load]);

  const toggleFlag = useCallback(async (locationId: string, field: keyof SubAccount, next: boolean) => {
    const name = FLAG_FIELDS[field as string];
    if (!name) return;

    setSavingId(locationId);
    setLocations((prev) => prev.map((l) => (l.locationId === locationId ? { ...l, [field]: next } : l)));
    try {
      await callApi(`sysadmin/gohighlevel/locations/${locationId}/flag`, HttpMethod.PUT, { name, value: next });
      toast.success("Saved.");
    } catch {
      setLocations((prev) => prev.map((l) => (l.locationId === locationId ? { ...l, [field]: !next } : l)));
      toast.error("Failed to save. Reverted.");
    } finally {
      setSavingId(null);
    }
  }, []);

  const data = useMemo(
    () => (gridFilter ? filterBy(orderBy(locations, sort), gridFilter) : orderBy(locations, sort)),
    [locations, sort, gridFilter],
  );

  const ToggleCell = useCallback(
    (field: keyof SubAccount) => (props: GridCellProps) => {
      const item = props.dataItem as SubAccount;
      const checked = Boolean(item[field]);
      return (
        <td>
          <div className="form-check form-switch m-0">
            <input
              type="checkbox"
              role="switch"
              className="form-check-input"
              checked={checked}
              disabled={savingId === item.locationId}
              onChange={(e) => toggleFlag(item.locationId, field, e.target.checked)}
            />
          </div>
        </td>
      );
    },
    [savingId, toggleFlag],
  );

  const ContactsCell = (props: GridCellProps) => {
    const v = (props.dataItem as SubAccount).contactCount;
    return <td style={{ textAlign: "right" }}>{v != null ? v.toLocaleString() : "—"}</td>;
  };

  // GHL still reports the app as installed in subaccounts it has paused or deleted, so a refused
  // location token is the only signal we get that one has gone away.
  const AccessCell = (props: GridCellProps) => {
    const since = (props.dataItem as SubAccount).inaccessibleSince;
    return (
      <td>
        {since ? (
          <span className="badge bg-danger" title={`GoHighLevel refused access since ${new Date(since).toLocaleString()}`}>
            No access
          </span>
        ) : (
          <span className="badge bg-success">OK</span>
        )}
      </td>
    );
  };

  const ScrubLimitCell = (props: GridCellProps) => {
    const v = (props.dataItem as SubAccount).scrubLimit;
    return <td style={{ textAlign: "right" }}>{v != null ? v.toLocaleString() : "—"}</td>;
  };

  const LastRunCell = (props: GridCellProps) => {
    const v = (props.dataItem as SubAccount).bulkScrubLastRunAt;
    return <td>{v ? new Date(v).toLocaleString() : "Never"}</td>;
  };

  const ActionCell = (props: GridCellProps) => (
    <td>
      <button
        className={styles.linkButton}
        onClick={() => navigate(`/sysadmin/gohighlevel/locations/${(props.dataItem as SubAccount).locationId}`)}
      >
        View detail
      </button>
    </td>
  );

  if (loading) return <Loading />;

  return (
    <div className={styles.container}>
      <GoHighLevelNav />
      <Link to="/sysadmin/gohighlevel/agencies" className={styles.backLink}>
        &larr; All agencies
      </Link>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>{agency?.agencyName}</h2>
          <p className={styles.pageSubtitle}>
            {agency?.isInstalled ? "Installed" : "Uninstalled"} · {locations.length} subaccounts ·{" "}
            {(agency?.totalContacts ?? 0).toLocaleString()} contacts · Scrubs Included in Plan:{" "}
            {agency?.scrubLimit != null ? agency.scrubLimit.toLocaleString() : "—"}
          </p>
        </div>
      </div>

      {settings && (
        <div className={styles.card}>
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="fw-semibold">Agency settings</span>
            <span className={`badge ${settings.isConnected ? "bg-success" : "bg-secondary"}`}>
              {settings.isConnected ? "Connected" : "Not connected"}
            </span>
            <span className={`badge ${settings.isActive ? "bg-success" : "bg-secondary"}`}>
              {settings.isActive ? "Active" : "Inactive"}
            </span>
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label className={styles.formLabel}>Agency name</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={settings.agencyName}
                onChange={(e) => updateSetting("agencyName", e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className={styles.formLabel}>DNC Scrub Login ID</label>
              <input
                type="password"
                autoComplete="off"
                className="form-control form-control-sm"
                value={settings.dncScrubLoginId}
                onChange={(e) => updateSetting("dncScrubLoginId", e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className={styles.formLabel}>DNC Scrub Campaign ID</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={settings.dncScrubCampaignId}
                onChange={(e) => updateSetting("dncScrubCampaignId", e.target.value)}
              />
            </div>
          </div>
          <div className={styles.formHint}>
            These agency credentials apply to every subaccount unless a subaccount overrides them.
          </div>
          <button className="btn btn-primary btn-sm mt-3" onClick={saveAgency} disabled={savingAgency}>
            {savingAgency ? "Saving…" : "Save agency settings"}
          </button>
        </div>
      )}

      <Grid
        className="sysadmin-grid"
        data={data}
        dataItemKey="locationId"
        sortable={true}
        resizable={true}
        sort={sort}
        onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
        filterable={true}
        filter={gridFilter}
        onFilterChange={(e: GridFilterChangeEvent) => setGridFilter(e.filter)}
        style={{ height: "calc(100vh - 220px)", width: "100%" }}
      >
        <Column field="locationName" title="Subaccount" width={260} />
        <Column field="inaccessibleSince" title="Access" width={120} filterable={false} cells={{ data: AccessCell }} />
        <Column field="contactCount" title="Contacts" width={130} filterable={false} cells={{ data: ContactsCell }} />
        <Column field="scrubLimit" title="Scrubs Included in Plan" width={130} filterable={false} cells={{ data: ScrubLimitCell }} />
        <Column field="bulkScrubEnabled" title="Bulk Scrub" width={130} filterable={false} cells={{ data: ToggleCell("bulkScrubEnabled") }} />
        <Column field="autoScrubOnContactCreate" title="Auto-scrub" width={130} filterable={false} cells={{ data: ToggleCell("autoScrubOnContactCreate") }} />
        <Column field="createNoteOnContactCreate" title="Create Note" width={130} filterable={false} cells={{ data: ToggleCell("createNoteOnContactCreate") }} />
        <Column field="bulkScrubSchedule" title="Schedule" width={130} />
        <Column field="bulkScrubLastRunAt" title="Last Run" width={190} filterable={false} cells={{ data: LastRunCell }} />
        <Column title="Actions" width={150} filterable={false} sortable={false} cells={{ data: ActionCell }} />
      </Grid>
    </div>
  );
};

export default AgencyDetail;
